// Lightweight, dependency-free syntax highlighter for the file types App
// Forge generates (TS/JS/JSX, JSON, CSS, HTML). Regex tokenizer over the whole
// file (so multi-line comments/template strings work), then split into lines.

export type TokenKind = 'comment' | 'string' | 'number' | 'keyword' | 'tag' | 'attr' | 'prop' | 'punct' | 'plain';
export interface Token {
  kind: TokenKind;
  text: string;
}

export type Lang = 'ts' | 'json' | 'css' | 'html' | 'md' | 'text';

export function langOf(path: string): Lang {
  const ext = path.split('.').pop()?.toLowerCase() ?? '';
  if (['ts', 'tsx', 'js', 'jsx', 'mjs', 'cjs'].includes(ext)) return 'ts';
  if (ext === 'json') return 'json';
  if (['css', 'scss', 'pcss'].includes(ext)) return 'css';
  if (['html', 'htm', 'svg', 'xml'].includes(ext)) return 'html';
  if (ext === 'md') return 'md';
  return 'text';
}

export const langLabel: Record<Lang, string> = {
  ts: 'TypeScript', json: 'JSON', css: 'CSS', html: 'HTML', md: 'Markdown', text: 'Text',
};

const TS_KEYWORDS =
  'import|from|export|default|const|let|var|function|return|if|else|for|while|do|switch|case|break|continue|type|interface|extends|implements|new|class|async|await|try|catch|finally|throw|true|false|null|undefined|as|of|in|typeof|keyof|void|this|enum|readonly|public|private|protected|static';

type Rule = [TokenKind, string];

const RULES: Record<Lang, Rule[]> = {
  ts: [
    ['comment', String.raw`\/\/[^\n]*|\/\*[\s\S]*?\*\/`],
    ['string', String.raw`'(?:\\.|[^'\\\n])*'|"(?:\\.|[^"\\\n])*"|\`(?:\\[\s\S]|[^\`\\])*\``],
    ['tag', String.raw`<\/?[A-Z][\w.]*|<\/?[a-z][\w-]*(?=[\s/>])|\/>`],
    ['keyword', String.raw`\b(?:${TS_KEYWORDS})\b`],
    ['number', String.raw`\b\d+(?:\.\d+)?\b`],
    ['prop', String.raw`\b[A-Za-z_$][\w$]*(?=\s*\()`],
  ],
  json: [
    ['prop', String.raw`"(?:\\.|[^"\\\n])*"(?=\s*:)`],
    ['string', String.raw`"(?:\\.|[^"\\\n])*"`],
    ['number', String.raw`-?\b\d+(?:\.\d+)?(?:[eE][+-]?\d+)?\b`],
    ['keyword', String.raw`\b(?:true|false|null)\b`],
  ],
  css: [
    ['comment', String.raw`\/\*[\s\S]*?\*\/`],
    ['string', String.raw`'(?:\\.|[^'\\\n])*'|"(?:\\.|[^"\\\n])*"`],
    ['keyword', String.raw`@[\w-]+|!important`],
    ['prop', String.raw`[\w-]+(?=\s*:[^:{}]*[;}])`],
    ['number', String.raw`#[0-9a-fA-F]{3,8}\b|-?\b\d+(?:\.\d+)?(?:px|rem|em|%|vh|vw|s|ms|deg)?\b`],
    ['tag', String.raw`[.#]?[A-Za-z][\w-]*(?=[^{};]*\{)`],
  ],
  html: [
    ['comment', String.raw`<!--[\s\S]*?-->`],
    ['tag', String.raw`<\/?[A-Za-z][\w-]*|\/?>|<!doctype[^>]*>`],
    ['attr', String.raw`\b[\w-:]+(?==)`],
    ['string', String.raw`"[^"]*"|'[^']*'`],
  ],
  md: [
    ['keyword', String.raw`^#{1,6} [^\n]*`],
    ['string', String.raw`\`[^\`\n]+\``],
    ['comment', String.raw`^>[^\n]*`],
  ],
  text: [],
};

const compiled = new Map<Lang, RegExp | null>();
function regexFor(lang: Lang): RegExp | null {
  if (!compiled.has(lang)) {
    const rules = RULES[lang];
    compiled.set(lang, rules.length ? new RegExp(rules.map(([, re]) => `(${re})`).join('|'), 'gm') : null);
  }
  return compiled.get(lang)!;
}

/** Tokenize `src` and return one token array per line. */
export function highlightLines(src: string, lang: Lang): Token[][] {
  const re = regexFor(lang);
  const tokens: Token[] = [];
  if (!re || src.length > 400_000) {
    tokens.push({ kind: 'plain', text: src });
  } else {
    const rules = RULES[lang];
    re.lastIndex = 0;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(src))) {
      if (m[0] === '') { re.lastIndex++; continue; }
      if (m.index > last) tokens.push({ kind: 'plain', text: src.slice(last, m.index) });
      const group = m.slice(1).findIndex((g) => g !== undefined);
      tokens.push({ kind: group >= 0 ? rules[group][0] : 'plain', text: m[0] });
      last = m.index + m[0].length;
    }
    if (last < src.length) tokens.push({ kind: 'plain', text: src.slice(last) });
  }
  const lines: Token[][] = [[]];
  for (const t of tokens) {
    const parts = t.text.split('\n');
    parts.forEach((part, i) => {
      if (i > 0) lines.push([]);
      if (part) lines[lines.length - 1].push({ kind: t.kind, text: part });
    });
  }
  return lines;
}

export const tokenClass: Record<TokenKind, string> = {
  comment: 'text-[#6b7289] italic',
  string: 'text-[#a5d6a7]',
  number: 'text-[#f9b872]',
  keyword: 'text-[#c792ea]',
  tag: 'text-[#7fb4ff]',
  attr: 'text-[#ffcb6b]',
  prop: 'text-[#82aaff]',
  punct: 'text-gray-500',
  plain: 'text-gray-300',
};
